import { css, Rule } from 'glamor'
import * as React from 'react'
import { FormattedMessage, defineMessages } from 'react-intl'
import * as PropTypes from 'prop-types'

import { isKey, isElement, notEmpty } from '../../utils'
import { useTheme } from '../../theme'

import { Popup } from '../popup'
import { Flex } from '../flex'

import getStyles from './styles'

const messages = defineMessages({
  more: {
    id: 'ui-kit.tabs.more',
    defaultMessage: 'More',
  },
})

interface ITabsProps {
  extra?: React.ReactNode
  children: React.ReactNode
  value: string | number | symbol
  hiddenValues?: (string | number | symbol)[]
  moreLabel?: React.ReactNode
  style?: Rule
  size?: 'medium' | 'small'
}

interface ITabsState {
  hiddenValues: (string | number | symbol)[]
  windowWidth: number
}

export class Tabs extends React.Component<ITabsProps, ITabsState> {
  static propTypes = {
    children: PropTypes.node.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.symbol]),
    hiddenValues: PropTypes.array,
    moreLabel: PropTypes.node,
    style: PropTypes.object,
    size: PropTypes.oneOf(['medium', 'small']),
  }

  constructor(props: ITabsProps) {
    super(props)

    this.tabs = React.createRef()
  }

  state = {
    hiddenValues: [],
    windowWidth: window.innerWidth,
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize)
    this.maybeCropList()
  }

  componentDidUpdate(_prevProps: ITabsProps, prevState: ITabsState) {
    this.maybeCropList(prevState.windowWidth - this.state.windowWidth)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize = () => {
    this.setState({
      windowWidth: window.innerWidth,
    })
  }

  tabs: React.RefObject<HTMLElement>

  maybeCropList(resize = 0) {
    const children = React.Children.toArray(this.props.children)

    if (!(this.tabs.current && this.tabs.current.firstElementChild && this.tabs.current.parentElement)) return

    const list = this.tabs.current.firstElementChild

    const { width: listWidth } = list.getBoundingClientRect()
    const { width: parentWidth } = this.tabs.current.parentElement.getBoundingClientRect()

    if (listWidth > parentWidth && this.state.hiddenValues.length === 0) {
      const { hiddenValues } = Array.from(list.children)
        .slice(0, this.state.hiddenValues.length > 0 ? -1 : list.children.length)
        .reverse()
        .reduce(
          ({ hiddenValues: accHiddenValues, totalSubtractedWidth, hideSuccedent }, child, index) => {
            if (listWidth - totalSubtractedWidth > parentWidth || hideSuccedent) {
              const { width: childWidth } = child.getBoundingClientRect()
              const childElement = children[children.length - 1 - index]
              const { value } = isElement(childElement) ? childElement.props : { value: null }

              return {
                totalSubtractedWidth: totalSubtractedWidth + childWidth,
                hiddenValues: accHiddenValues.concat(value),
                hideSuccedent: listWidth - (totalSubtractedWidth + childWidth) <= parentWidth && !hideSuccedent,
              }
            }

            return {
              hiddenValues: accHiddenValues,
              totalSubtractedWidth,
              hideSuccedent,
            }
          },
          { hiddenValues: [], totalSubtractedWidth: 0, hideSuccedent: false }
        )

      if (hiddenValues.length === this.state.hiddenValues.length) return

      this.setState({
        hiddenValues,
        windowWidth: window.innerWidth,
      })
    } else if (resize !== 0) {
      this.setState({ hiddenValues: [] })
    }
  }

  render() {
    return <TabsInner {...this.props} hiddenValues={this.state.hiddenValues} innerRef={this.tabs} />
  }
}

const cloneChildren = (
  children: React.ReactNode,
  currentValue: string | number | symbol,
  hiddenValues: (string | number | symbol)[],
  moreLabel: React.ReactNode,
  size: 'medium' | 'small'
) => {
  const childrenArr = React.Children.toArray(children)
    .filter(child => isElement(child))
    .filter(notEmpty) as React.ReactElement[]

  const navChildren = childrenArr

    .filter(({ props }: React.ReactElement) => !hiddenValues.includes(props.value))
    .map((child: React.ReactElement) =>
      React.cloneElement(child, {
        active: child.props.value === currentValue,
        key: `visible-item-${child.props.value}`,
        size,
      })
    )

  const moreItems = childrenArr
    .filter(child => isElement(child))
    .filter(({ props }: React.ReactElement) => hiddenValues.includes(props.value))
    .map((child: React.ReactElement) => (
      <MoreItem
        {...child.props}
        active={child.props.value === currentValue}
        key={`hidden-item-${child.props.value}`}
        size={size}
      />
    ))

  const moreListActive = moreItems.some(child => child.props.value === currentValue)

  return moreItems.length === 0
    ? navChildren
    : navChildren.concat(
        <MoreList
          active={moreListActive}
          key="more-list"
          label={moreLabel || <FormattedMessage {...messages.more} />}
          size={size}
        >
          {moreItems}
        </MoreList>
      )
}

interface ITabsInnerProps {
  children?: React.ReactNode
  value: string | number | symbol
  hiddenValues: (string | number | symbol)[]
  moreLabel?: React.ReactNode
  style?: Rule
  extra?: React.ReactNode
  innerRef?: React.RefObject<HTMLElement>
  size?: 'medium' | 'small'
}

const TabsInner = ({
  children,
  value,
  hiddenValues,
  moreLabel,
  extra,
  style,
  innerRef,
  size = 'medium',
}: ITabsInnerProps) => {
  const styles = getStyles(useTheme())
  const sizeKey = `size-${size}`
  return (
    <Flex
      component="nav"
      innerRef={innerRef}
      row
      style={[styles.wrapper.base, isKey(styles.wrapper, sizeKey) && styles.wrapper[sizeKey], style]}
    >
      <ul {...css([styles.list.base, isKey(styles.list, sizeKey) && styles.list[sizeKey]])}>
        {children && cloneChildren(children, value, hiddenValues, moreLabel, size)}
      </ul>
      {extra}
    </Flex>
  )
}

TabsInner.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.symbol]),
  hiddenValues: PropTypes.array,
  moreLabel: PropTypes.node,
  style: PropTypes.object,
  extra: PropTypes.node,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  size: PropTypes.oneOf(['medium', 'small']),
}

interface ITabsItemProps {
  children: React.ReactNode
  value: string | number | symbol
  active?: boolean
  disabled?: boolean
  style?: Rule
  onClick?: () => void
  component?:
    | string
    | React.ComponentType<{
        onClick?: () => void
        ref?: React.RefObject<{}>
      }>
  size?: 'medium' | 'small'
}

export function TabsItem({
  children,
  active,
  disabled,
  style,
  onClick,
  component: Component = 'button',
  size,
  ...additional
}: ITabsItemProps) {
  const styles = getStyles(useTheme())
  const sizeKey = `size-${size}`
  return (
    <li {...css(styles.item.base)}>
      <Component
        onClick={!active && !disabled ? onClick : undefined}
        {...css(
          styles.itemLabel.base,
          isKey(styles.itemLabel, sizeKey) && styles.itemLabel[sizeKey],
          !disabled && styles.itemLabel.clickable,
          active && styles.itemLabel.active,
          style
        )}
        {...additional}
      >
        {children}
        {active && (
          <span
            {...css([styles.itemActiveBar.base, isKey(styles.itemActiveBar, sizeKey) && styles.itemActiveBar[sizeKey]])}
          />
        )}
      </Component>
    </li>
  )
}

TabsItem.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.symbol]).isRequired,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  style: PropTypes.object,
  onClick: PropTypes.func,
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  size: PropTypes.oneOf(['medium', 'small']),
}

interface IMoreListProps {
  children?: React.ReactNode
  active?: boolean
  size?: 'medium' | 'small'
  label?: React.ReactNode
}

const MoreList = ({ children, active, size, label = 'More' }: IMoreListProps) => {
  const styles = getStyles(useTheme())
  const sizeKey = `size-${size}`
  return (
    <Popup
      renderPopup={() => <ul {...css(styles.dropdown.base)}>{children}</ul>}
      renderToggle={({ toggleOpen, ref }) => (
        <li
          {...css(
            styles.item.base,
            active && styles.item.active,
            !active && styles.moreItem.normal,
            active && styles.moreItem.active
          )}
        >
          <button
            {...css(
              styles.moreLabel.base,
              isKey(styles.moreLabel, sizeKey) && styles.moreLabel[sizeKey],
              active && styles.item.active
            )}
            onClick={() => toggleOpen()}
            ref={ref}
          >
            {label} <ChevronIcon active={active} />
            {active && <span {...css(styles.itemActiveBar.base, styles.itemActiveBar.moreList)} />}
          </button>
        </li>
      )}
    />
  )
}

interface IMoreItemProps {
  children: React.ReactNode
  active?: boolean
  style?: Rule
  onClick?: () => void
}

const MoreItem = ({ children, active, style, onClick, ...additional }: IMoreItemProps) => {
  const styles = getStyles(useTheme())
  return (
    <li {...additional}>
      <button onClick={onClick} {...css(styles.dropdownItem.base, active && styles.item.active, style)}>
        {children}
      </button>
    </li>
  )
}

MoreItem.propTypes = {
  children: PropTypes.node,
  active: PropTypes.bool,
  style: PropTypes.object,
}

interface IChevronIconProps {
  active?: boolean
}

const ChevronIcon = ({ active }: IChevronIconProps) => {
  const styles = getStyles(useTheme())
  return (
    <i {...css(styles.chevronIcon.base)}>
      <svg height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" fillRule="evenodd">
          <path d="M0 0h20v20H0z" />
          <path
            d="M10.71 13.706a1.003 1.003 0 0 1-1.419 0L6.295 10.71a1 1 0 0 1-.002-1.416 1.005 1.005 0 0 1 1.418.004l2.29 2.29 2.289-2.29a1 1 0 0 1 1.418-.004c.39.39.39 1.025-.002 1.416l-2.996 2.997z"
            {...css(active ? styles.chevronIconPath.active : styles.chevronIconPath.normal)}
          />
        </g>
      </svg>
    </i>
  )
}

ChevronIcon.propTypes = {
  active: PropTypes.bool,
}
